import { TafProfileInfo } from '../types/network';

/** The TAF type that carries per-meter live power values (one TAF-10 == one Zähler). */
export const TAF10_TYPE = 'TAF-10';

/** Number of a TAF type ("TAF-7", "TAF7", "taf-10" → 7 / 10); null when unknown. */
export const tafNumber = (tafType: string | undefined | null): number | null => {
  const m = /^\s*taf\s*-?\s*(\d+)\s*$/i.exec(tafType ?? '');
  return m ? Number(m[1]) : null;
};

/** Display form of a TAF type: "TAF 7". */
export const tafLabel = (tafType: string | undefined | null): string => {
  const n = tafNumber(tafType);
  return n != null ? `TAF ${n}` : (tafType || 'TAF ?');
};

/**
 * Register / billing profiles TAF 1–8 (TAF 1 Zählerstand je Abrechnungsperiode,
 * TAF 2 Tarifregister, TAF 7 Zählerstandsgang …). Never live data; their values
 * are loaded on demand from the Messstellenbetreiber and never stored
 * (backend `MeasurementConstants.IsRegisterTaf`).
 */
export const isRegisterTaf = (tafType: string | undefined | null): boolean => {
  const n = tafNumber(tafType);
  return n != null && n >= 1 && n <= 8;
};

/** TAF 7 = Zählerstandsgang (15-min register readings) — shown as a load curve. */
export const isLoadProfileTaf = (tafType: string | undefined | null): boolean => tafNumber(tafType) === 7;

/** What a TAF profile delivers, in one line (BSI TR-03109-1 Tarifanwendungsfälle). */
export const tafPurpose = (tafType: string | undefined | null): string => {
  switch (tafNumber(tafType)) {
    case 1: return 'Zählerstände je Abrechnungsperiode';
    case 2: return 'Zählerstände je Tarifregister';
    case 3: return 'Lastvariable Tarifregister';
    case 4: return 'Verbrauchsvariable Tarifregister';
    case 5: return 'Ereignisvariable Tarifregister';
    case 6: return 'Ablesung im Bedarfsfall';
    case 7: return 'Zählerstandsgang (15-Minuten-Werte)';
    case 8: return 'Extremwerte';
    case 9: return 'Ist-Einspeisung';
    case 10: return 'Netzzustandsdaten (Live-Werte)';
    case 14: return 'Hochfrequente Messwerte';
    default: return 'Messprofil';
  }
};

/** All TAF profiles of ONE physical meter (Zähler), grouped for display. */
export interface MeterGroup {
  /** Stable grouping key (Zählernummer > MeLo > logical-name suffix). */
  key: string;
  /** Zählernummer, wenn die Registry sie liefert. */
  meterDeviceNumber?: string;
  /** Erste bekannte Messlokation (MeLo) dieses Zählers. */
  meLoId?: string;
  /** Alle MeLos der Profile (ein Zähler kann Bezug und Einspeisung als getrennte MeLos führen). */
  meLoIds: string[];
  /** Alle TAF-Profile dieses Zählers (TAF-7, TAF-10, ...). */
  profiles: TafProfileInfo[];
  /** Das (erste) TAF-10-Profil (Live-Werte) dieses Zählers, falls vorhanden. */
  liveProfile?: TafProfileInfo;
  /** Alle TAF-10-Profile dieses Zählers (selten mehrere). */
  liveProfiles: TafProfileInfo[];
}

const isTaf10 = (p: TafProfileInfo): boolean => tafNumber(p.tafType) === 10;

const normId = (id: string | undefined): string | undefined => (id ? id.trim().toUpperCase() : undefined);

/** Logical-name fallback key: "taf{NN}{suffix}" — the suffix is identical for the profiles of one meter in some registries. */
const logicalSuffix = (p: TafProfileInfo): string => {
  const suffix = p.tafLogicalName.replace(/^taf\d+/i, '');
  return suffix || p.tafLogicalName;
};

/**
 * Grouping key for a TAF profile (single profile, no siblings considered):
 * Zählernummer, then MeLo, then the logical-name suffix.
 */
export const meterKeyForProfile = (p: TafProfileInfo): string => {
  if (p.meterDeviceNumber) return `meter:${p.meterDeviceNumber}`;
  if (p.meLoId) return `melo:${normId(p.meLoId)}`;
  return `logical:${logicalSuffix(p)}`;
};

/**
 * Groups a SMGW's TAF profiles per physical meter (Zähler).
 * One SMGW can serve several meters, and one meter can carry several TAF
 * profiles (TAF-1 + TAF-7 + TAF-10) — rendering one card per TAF profile would
 * show the same meter multiple times. A profile joins a group with the same
 * Zählernummer; without one (or without a match) it joins by MeLo, then by the
 * logical-name suffix — but never a group that carries a DIFFERENT Zählernummer
 * (two meters can share a MeLo). The live TAF-10 profile often brings the
 * Zählernummer while the register profiles known from the database only carry
 * the MeLo. Profiles with a Zählernummer are placed first, so the result does
 * not depend on the input order.
 */
export const groupTafProfilesByMeter = (profiles: TafProfileInfo[]): MeterGroup[] => {
  const groups: MeterGroup[] = [];
  const byMeter = new Map<string, MeterGroup>();
  const byMelo = new Map<string, MeterGroup>();
  const byLogical = new Map<string, MeterGroup>();
  const ordered = [...profiles].sort((a, b) =>
    Number(!!b.meterDeviceNumber) - Number(!!a.meterDeviceNumber) || a.tafLogicalName.localeCompare(b.tafLogicalName));
  const compatible = (g: MeterGroup | undefined, meterNo: string | undefined) =>
    g && (!meterNo || !g.meterDeviceNumber || g.meterDeviceNumber === meterNo) ? g : undefined;
  for (const profile of ordered) {
    const meterNo = profile.meterDeviceNumber;
    const melo = normId(profile.meLoId);
    const logical = logicalSuffix(profile);
    let group = (meterNo ? byMeter.get(meterNo) : undefined)
      ?? compatible(melo ? byMelo.get(melo) : undefined, meterNo)
      ?? compatible(byLogical.get(logical), meterNo);
    if (!group) {
      group = { key: meterKeyForProfile(profile), profiles: [], meLoIds: [], liveProfiles: [] };
      groups.push(group);
    }
    group.profiles.push(profile);
    group.meterDeviceNumber ??= meterNo;
    group.meLoId ??= profile.meLoId;
    if (profile.meLoId && !group.meLoIds.some(m => normId(m) === melo)) group.meLoIds.push(profile.meLoId);
    if (isTaf10(profile)) {
      group.liveProfiles.push(profile);
      group.liveProfile ??= profile;
    }
    if (meterNo && !byMeter.has(meterNo)) byMeter.set(meterNo, group);
    if (melo && !byMelo.has(melo)) byMelo.set(melo, group);
    if (!byLogical.has(logical)) byLogical.set(logical, group);
  }
  for (const g of groups) {
    // Live profile first, then by TAF number, so the tabs read TAF 10 · TAF 7 · TAF 1.
    g.profiles.sort((a, b) => (tafNumber(b.tafType) === 10 ? 1 : 0) - (tafNumber(a.tafType) === 10 ? 1 : 0)
      || (tafNumber(b.tafType) ?? 0) - (tafNumber(a.tafType) ?? 0)
      || a.tafLogicalName.localeCompare(b.tafLogicalName));
    // Stable key: the first logical name, independent of whether the Zählernummer has arrived yet
    g.key = `meter:${[...g.profiles].map(p => p.tafLogicalName).sort()[0]}`;
  }
  // Meters with live values first, then stable by key for deterministic order
  return groups.sort((a, b) =>
    Number(!!b.liveProfile) - Number(!!a.liveProfile) || a.key.localeCompare(b.key));
};

/** Number of physical meters (Zähler) behind a list of TAF profiles. */
export const countMeters = (profiles: TafProfileInfo[] | undefined): number =>
  profiles && profiles.length > 0 ? groupTafProfilesByMeter(profiles).length : 0;

/**
 * Kompakte Anzeige einer Messlokations-ID (MeLo, 33 Zeichen). Der gemeinsame
 * Präfix aller Geschwister-MeLos (z. B. Bezug/Einspeise-Paar eines Zählers)
 * wird abgeschnitten, damit die UNTERSCHEIDENDE Stelle sichtbar bleibt:
 * "…50…0088" vs "…51…0088" statt zweimal "…00000088". Ohne Geschwister
 * bleiben die letzten 8 Zeichen. Der volle Wert gehört in den Tooltip.
 */
export const shortMeloLabel = (melo: string, siblings: readonly string[] = []): string => {
  const others = siblings.filter(m => m && m !== melo);
  if (others.length === 0) return `…${melo.slice(-8)}`;
  let common = 0;
  const maxCommon = Math.min(melo.length, ...others.map(m => m.length));
  while (common < maxCommon && others.every(m => m[common] === melo[common])) common++;
  // Nie mehr als bis zur letzten 8er-Gruppe abschneiden.
  common = Math.min(common, Math.max(0, melo.length - 8));
  const rest = melo.slice(common);
  if (rest.length <= 10) return `…${rest}`;
  return `…${rest.slice(0, 2)}…${rest.slice(-4)}`;
};

/** Messrichtung eines Messpunkts: Netzbezug oder Einspeisung (Erzeugung). */
export type MeterDirection = 'bezug' | 'einspeisung';

/**
 * Direction of a meter group's Messpunkt, derived from its TAF logical names
 * (e.g. "taf7_einspeisung_034576"). Registries that don't encode a direction
 * in the name yield undefined — callers keep a neutral display then.
 */
export const meterDirection = (group: MeterGroup): MeterDirection | undefined => {
  for (const profile of group.profiles) {
    const name = profile.tafLogicalName?.toLowerCase() ?? '';
    if (name.includes('einspeis')) return 'einspeisung';
    if (name.includes('bezug')) return 'bezug';
  }
  return undefined;
};
